import { Button, ChevronDownIcon, ChevronUpIcon, ExpandableText, Heading, RedirectIcon, Tag } from "@r360/library";
import classNames from "classnames";
import produce from "immer";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ProductCardImage, ProductCardItem } from ".";
import {
    getInspectionDataForGroup,
    getInspectionDataForProduct,
    getLowestPriceWithOrdinaryPrice,
} from "../../BusinessUtils";
import * as Constants from "../../Constants";
import { formatPrice } from "../../Helper";
import useAppSelector from "../../hooks/useAppSelector";
import useItemInCart from "../../hooks/useItemInCart";
import useTranslate from "../../hooks/useTranslate";
import { optionsSelector } from "../../Selectors";
import { agesSelector } from "../../selectors/Selectors";
import { addProduct, addProductCount, addProductGuests } from "../../store/actions/checkoutCart";
import {
    TGeneralAges,
    TGroup,
    TImage,
    TItem,
    TPackageGroup,
    TStringIndexedObjectWithNumbers,
    TThumbnail,
} from "../../store/types";
import { RichText } from "../UI";
import "./ProductCard.scss";

export type TGuests = TStringIndexedObjectWithNumbers;

export type TAddToCartData = {
    item: TItem;
    quantity: number;
};

export type TItemColumn = {
    title?: string | JSX.Element;
    icon?: JSX.Element;
};

type TProductCard = {
    group?: TGroup | TPackageGroup;
    reservationType?: string;
    images: TImage[];
    title: string;
    description: string;
    currency: string;
    getItemColumns?: (arg0: TItem, arg1?: TItem[]) => TItemColumn[];
    showItemsButtonText?: string;
    renderAdditionalInformation?: (arg0: TGroup) => JSX.Element | null | string | number;
    fallbackImage?: string;
    onAddToCartQuantityChange?: (arg0: number) => void;
    unlimitedBookings?: boolean;
    searchWithoutPrice?: boolean;
    decorateItemBeforeCart?: (item: TItem) => void;
    withMoreImageButton?: boolean;
    newCartItemOnEveryAdd?: boolean;
    isPackage?: boolean;
    isExternalProduct?: boolean;
    externalProductLink?: string;
    externalProductAccessibility?: string;
    hideItemsButton?: boolean;
};

export const ProductCard = ({
    group,
    reservationType,
    images,
    title,
    description,
    currency,
    getItemColumns,
    showItemsButtonText,
    renderAdditionalInformation,
    fallbackImage,
    onAddToCartQuantityChange,
    unlimitedBookings = false,
    searchWithoutPrice = false,
    decorateItemBeforeCart,
    withMoreImageButton = false,
    newCartItemOnEveryAdd = false,
    isExternalProduct = false,
    externalProductLink,
    externalProductAccessibility,
    isPackage = false,
    hideItemsButton,
}: TProductCard) => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const getItemInCart = useItemInCart();

    const generalAges: TGeneralAges = useAppSelector(agesSelector);
    const lang = useAppSelector(state => state.clientData?.lang);
    const showImageDescriptions = useAppSelector(optionsSelector).layout?.show_image_descriptions || false;

    const cardRef = useRef<HTMLDivElement | null>(null);
    const imageRef = useRef<HTMLDivElement | null>(null);
    const [itemsExpanded, setItemsExpanded] = useState(false);
    const [addToCartData, setAddToCartData] = useState<{ [key: number]: TAddToCartData }>({});
    const [addToCartSuccessId, setAddToCartSuccessId] = useState<number | null>(null);
    const [cardHeightIsLargerThanImage, setCardHeightIsLargerThanImage] = useState(false);
    const [itemsInCartQuantity, setItemsInCartQuantity] = useState(0);
    const { isMobile } = useAppSelector(state => state.window);

    const items = group?.items ?? [];
    const { price, ordinaryprice } = getLowestPriceWithOrdinaryPrice(items, generalAges);
    const showCampaignPrice = ordinaryprice > 0 && price < ordinaryprice;
    const firstItem = items?.[0] ?? undefined;
    const addToCartSuccesDuration = 1500;

    const addToCartDataQuantity = Object.values(addToCartData).reduce((acc, item) => {
        return acc + item.quantity;
    }, 0);

    const showMoreImages = withMoreImageButton && !!images && images.length > 0;

    const groupOrItemInspectionData =
        group && "isGrouped" in group && group.isGrouped && group.grouppoollid > 0
            ? getInspectionDataForGroup(group)
            : firstItem
            ? getInspectionDataForProduct(firstItem)
            : {};

    let mainImage: TImage | TThumbnail | undefined = undefined;

    if (isExternalProduct) {
        // If external product, use the first image from the images array.
        mainImage = images?.[0];
    } else if (group && "isGrouped" in group && group.isGrouped && group.groupitem?.thumbnail) {
        // If group, use the thumbnail from the group.
        mainImage = group.groupitem.thumbnail;
    } else if (firstItem?.images && firstItem.images.length > 0) {
        // Otherwise, use the first image from the first item.
        mainImage = firstItem?.images[0];
    } else if (!!images && images.length > 0) {
        mainImage = images[0];
    }

    const { minDays, maxDays } = items.reduce(
        (acc, item) => {
            if (!acc.minDays || item.days < acc.minDays) {
                acc.minDays = item.days;
            }

            if (item.days > acc.maxDays) {
                acc.maxDays = item.days;
            }

            return acc;
        },
        { minDays: 0, maxDays: 0 }
    );

    const daysText =
        minDays === maxDays
            ? `${minDays} ${t(minDays === 1 ? "book.general.day" : "book.general.days").toLowerCase()}`
            : `${minDays} - ${maxDays} ${t("book.general.days").toLowerCase()}`;

    const anyItemHasOccations = items.some(item => item.occations?.length > 0);
    let showDays = minDays > 0;

    if (minDays === 1 && maxDays === 1 && anyItemHasOccations) {
        showDays = false;
    }

    /*
     * Set cardHeightIsLargerThanImage state based on the height of the card and the image
     * If the card is larger than the image, we need to do some additional styling.
     */
    useEffect(() => {
        if (cardRef.current) {
            const observer = new ResizeObserver(() => {
                if (cardRef.current && imageRef.current) {
                    setCardHeightIsLargerThanImage(cardRef.current?.clientHeight > imageRef.current?.clientHeight);
                }
            });

            observer.observe(cardRef.current);
            return () => {
                observer.disconnect();
            };
        }
    }, []);

    useEffect(() => {
        onAddToCartQuantityChange?.(addToCartDataQuantity);
        // eslint-disable-next-line
    }, [addToCartDataQuantity]);

    // Calculate the total quantity of items already in the cart.
    useEffect(() => {
        if (!itemsExpanded && items.length > 0) {
            const itemsInCart = items.reduce((acc, item) => {
                const { itemInCartQuantity } = getItemInCart(item);
                return acc + itemInCartQuantity;
            }, 0);

            setItemsInCartQuantity(itemsInCart);
        }
        // eslint-disable-next-line
    }, [itemsExpanded]);

    const changeGroupQuantity = (item: TItem, quantity: number) => {
        setAddToCartData(prevState => {
            if (prevState && quantity > 0) {
                return { ...prevState, [item.id]: { item, quantity } };
            }
            const { [item.id]: _, ...rest } = prevState;
            return rest;
        });
    };

    const handleAddToCart = (item: TItem, count: number, guests?: TGuests) => {
        let decoratedItem = decorateItemBeforeCart ? decorateItemBeforeCart(item) : item;

        if (guests) {
            // Add guests to item before it's added to cart.
            decoratedItem = produce(decoratedItem, (draft: TItem) => {
                draft.info
                    ? (draft.info.guests = { ...guests, totalGuests: count })
                    : (draft.info = { guests: { ...guests, totalGuests: count } });
                draft.count = count;
            });
            dispatch(addProductGuests(decoratedItem));
        } else if (newCartItemOnEveryAdd) {
            dispatch(addProduct(decoratedItem));
        } else {
            dispatch(addProductCount(decoratedItem, count, true));
        }

        setAddToCartSuccessId(item.id);

        setTimeout(() => {
            setAddToCartSuccessId(null);
            changeGroupQuantity(item, 0);
        }, addToCartSuccesDuration);
    };

    const groupedItems = items.reduce((group: Record<string, TItem[]>, item) => {
        const { arrdate } = item;
        group[arrdate] = group[arrdate] ?? [];
        group[arrdate].push(item);
        return group;
    }, {});

    return (
        <article
            ref={cardRef}
            className={classNames("r360-product-card", {
                "r360-product-card--items-expanded": itemsExpanded,
            })}
            {...groupOrItemInspectionData}
        >
            <div className="r360-product-card__primary-section">
                <ProductCardImage
                    mainImage={mainImage}
                    images={images}
                    showMoreImages={showMoreImages}
                    showImageDescriptions={showImageDescriptions}
                    fallbackImage={fallbackImage}
                    lang={lang}
                    imageRef={imageRef}
                    cardHeightIsLargerThanImage={cardHeightIsLargerThanImage}
                    externalProductAccessibility={externalProductAccessibility}
                />
                <div
                    className={classNames({
                        "r360-product-card__info": !hideItemsButton,
                        "r360-product-card__info-full-width": hideItemsButton,
                    })}
                >
                    <div className="u-d-flex u-gap-12 u-mb-6 u-align-items-start">
                        <Heading type="h2" styleAs="h3">
                            {title}
                        </Heading>
                        {!!firstItem?.promotionCode && (
                            <Tag theme="yellow" size="mini" text={t("book.general.campaign")} className="u-mt-6" />
                        )}
                        {showDays && <Tag size="mini" text={daysText} className="u-mt-6" />}
                    </div>
                    {group && renderAdditionalInformation && (
                        <div className="u-d-flex u-gap-6 u-align-items-center u-mb-6">
                            {renderAdditionalInformation(group)}
                        </div>
                    )}
                    <ExpandableText
                        text={description ?? ""}
                        collapsedButtonText={t("book.general.read_more")}
                        expandedButtonText={t("book.general.read_less")}
                        collapsedLineClamp={isMobile ? 3 : 2}
                        inlineButton
                        renderText={(text: string) => <RichText content={text} />}
                    />
                </div>
                {(!searchWithoutPrice || isPackage) && !isExternalProduct && (
                    <div className="r360-product-card__price-wrapper">
                        {!searchWithoutPrice && !isExternalProduct && (
                            <>
                                {items.length > 1 ? (
                                    <div className="r360-product-card__price-from">
                                        <span>{t("book.general.price_from")}</span>
                                    </div>
                                ) : (
                                    <div className="r360-product-card__price-from">
                                        <span>{t("book.general.price")}</span>
                                    </div>
                                )}
                                <div
                                    className={classNames("r360-product-card__price", {
                                        "r360-product-card__price--campaign": showCampaignPrice,
                                    })}
                                >
                                    <span>{formatPrice(price, currency)}</span>
                                </div>
                                {showCampaignPrice && (
                                    <span className="r360-product-card__ordinary-price">
                                        {formatPrice(ordinaryprice, currency)}
                                    </span>
                                )}
                            </>
                        )}

                        <>
                            {!hideItemsButton && (
                                <Button
                                    type="secondary"
                                    buttonSize="small"
                                    rightIcon={itemsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    onClick={() => setItemsExpanded(prevState => !prevState)}
                                    badgeCount={
                                        !itemsExpanded && addToCartDataQuantity > 0 ? addToCartDataQuantity : null
                                    }
                                    secondaryBadgeCount={
                                        !itemsExpanded && itemsInCartQuantity > 0 ? itemsInCartQuantity : null
                                    }
                                >
                                    {showItemsButtonText}
                                </Button>
                            )}
                        </>
                    </div>
                )}
                {isExternalProduct && (
                    <div className="r360-product-card__price-wrapper">
                        <p style={{ textAlign: "right" }}>{t("book.general.sold_externally")}</p>

                        {externalProductLink && (
                            <Button
                                link
                                externalLink
                                linkTo={externalProductLink}
                                rightIcon={<RedirectIcon />}
                                buttonClassName="u-mt-12"
                            >
                                {t("book.general.book_here")}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {itemsExpanded && reservationType === Constants.productTypeNames.ACTIVITY && (
                <div>
                    {Object.keys(groupedItems).map(key => (
                        <ProductCardItem
                            key={key}
                            groupedItem={groupedItems[key]}
                            item={groupedItems[key][0]}
                            getItemColumns={getItemColumns}
                            handleAddToCart={handleAddToCart}
                            addToCartSuccessId={addToCartSuccessId}
                            addToCartData={addToCartData}
                            changeGroupQuantity={changeGroupQuantity}
                            unlimitedBookings={unlimitedBookings}
                            searchWithoutPrice={searchWithoutPrice}
                        />
                    ))}
                </div>
            )}
            {itemsExpanded && reservationType !== Constants.productTypeNames.ACTIVITY && (
                <div>
                    {items.map(item => (
                        <ProductCardItem
                            key={item.id}
                            item={item}
                            getItemColumns={getItemColumns}
                            handleAddToCart={handleAddToCart}
                            addToCartSuccessId={addToCartSuccessId}
                            addToCartData={addToCartData}
                            changeGroupQuantity={changeGroupQuantity}
                            unlimitedBookings={unlimitedBookings}
                            searchWithoutPrice={searchWithoutPrice}
                            isPackage={isPackage}
                        />
                    ))}
                </div>
            )}
        </article>
    );
};
